import { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import authService from '../services/authService';
import userService from '../services/userService';
import { handleApiError } from '../services/utilsService';
import { SECURITY_CONFIG } from '../config';
import { info, warn, error as logError } from '../utils/logger';

// Crear el contexto
const AuthContext = createContext();

// Proveedor del contexto
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVerified, setIsVerified] = useState(true); // Asumimos que está verificado por defecto
  const [inactivityTimer, setInactivityTimer] = useState(null);

  const navigate = useNavigate();

  // Función para iniciar el temporizador de inactividad (simplificada)
  const startInactivityTimer = useCallback(() => {
    const timer = setInterval(() => {
      if (authService.checkInactivity()) {
        authService.clearAuth();
        setUser(null);
        navigate('/login?session=expired');
      }
    }, 60000);

    setInactivityTimer(timer);
    return timer;
  }, [navigate]);

  // Función para actualizar la última actividad del usuario
  const updateActivity = useCallback(() => {
    if (authService.isAuthenticated()) {
      authService.updateLastActivity();
    }
  }, []);

  // Cargar usuario al iniciar y configurar eventos de actividad
  useEffect(() => {
    let timer = null;

    const loadUser = async () => {
      try {
        const currentUser = authService.getCurrentUser();

        if (currentUser) {
          // Primero establecer el usuario para que isAuthenticated() funcione
          setUser(currentUser);

          // Verificar si el token ha expirado
          if (authService.isTokenExpired()) {
            try {
              // Intentar renovar el token
              await authService.refreshToken();
              // Después de renovar, obtener el usuario actualizado
              const updatedUser = authService.getCurrentUser();
              if (updatedUser) {
                setUser(updatedUser);
              }
            } catch (error) {
              logError('Error al renovar el token', { error: error.message, stack: error.stack });
              authService.clearAuth();
              setUser(null);
              setLoading(false);
              navigate('/login?session=expired');
              return;
            }
          }

          // Verificar si el usuario ha estado inactivo por demasiado tiempo
          if (authService.checkInactivity()) {
            authService.clearAuth();
            setUser(null);
            setLoading(false);
            navigate('/login?session=expired');
            return;
          }

          // Verificar si el email está verificado (si es requerido)
          if (SECURITY_CONFIG.REQUIRE_EMAIL_VERIFICATION && !currentUser.emailVerified) {
            setIsVerified(false);
          } else {
            setIsVerified(true);
          }

          // Actualizar la última actividad
          authService.updateLastActivity();

          // Iniciar el temporizador de inactividad
          timer = startInactivityTimer();
        }
      } catch (error) {
        logError('Error loading user', { error: error.message, stack: error.stack });
        // En caso de error, limpiar la autenticación
        authService.clearAuth();
        setUser(null);
      } finally {
        // Siempre establecer loading a false al final
        setLoading(false);
      }
    };

    loadUser();

    // Configurar eventos para detectar actividad del usuario
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    const handleUserActivity = () => {
      if (authService.isAuthenticated()) {
        authService.updateLastActivity();
      }
    };

    // Añadir event listeners para detectar actividad
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleUserActivity);
    });

    // Limpiar event listeners y temporizador al desmontar
    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });

      if (timer) {
        clearInterval(timer);
      }
    };
  }, [navigate]); // Solo navigate como dependencia para evitar bucles

  // Registrar usuario
  const register = async (userData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.register(userData);

      // Normalizar datos del usuario
      const normalizedUser = {
        ...response.user,
        // Asegurar compatibilidad entre campos
        birthDate: response.user.birthDate || response.user.birthdate,
        birthdate: response.user.birthDate || response.user.birthdate,
        walletBalance: response.user.wallet || response.user.walletBalance || 0,
        isActive: response.user.active !== undefined ? response.user.active : response.user.isActive,
      };

      setUser(normalizedUser);

      // Verificar si se requiere verificación de email
      if (SECURITY_CONFIG.REQUIRE_EMAIL_VERIFICATION && !normalizedUser.emailVerified) {
        setIsVerified(false);
      } else {
        setIsVerified(true);
      }

      // Iniciar el temporizador de inactividad
      startInactivityTimer();

      // Actualizar la última actividad
      updateActivity();

      return response;
    } catch (error) {
      console.log('Error en AuthContext register:', error);
      const errorMessage = handleApiError(error, 'Error al registrar usuario');
      setError(errorMessage);

      // Crear un error con el mensaje procesado para que el componente lo pueda usar
      const processedError = new Error(errorMessage);
      processedError.response = error.response;
      processedError.originalError = error;

      throw processedError;
    } finally {
      setLoading(false);
    }
  };

  // Verificar email
  const verifyEmail = async (token) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.verifyEmail(token);

      // Actualizar el usuario con el email verificado
      if (user) {
        setUser({ ...user, emailVerified: true });
      }

      setIsVerified(true);
      return response;
    } catch (error) {
      const errorMessage = handleApiError(error, 'Error al verificar email');
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Reenviar email de verificación
  const resendVerificationEmail = async (email) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.resendVerificationEmail(email || (user && user.email));
      return response;
    } catch (error) {
      const errorMessage = handleApiError(error, 'Error al reenviar email de verificación');
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Iniciar sesión
  const login = async (credentials, rememberMe = false) => {
    setLoading(true);
    setError(null);
    try {
      // El bloqueo ahora se maneja en el backend

      const response = await authService.login(credentials, rememberMe);
      setUser(response.user);

      // Verificar si se requiere verificación de email
      if (SECURITY_CONFIG.REQUIRE_EMAIL_VERIFICATION && !response.user.emailVerified) {
        setIsVerified(false);
      } else {
        setIsVerified(true);
      }

      // Iniciar el temporizador de inactividad
      startInactivityTimer();

      // Actualizar la última actividad
      updateActivity();

      // Verificar si se requiere completar el perfil
      if (response.requiresProfileCompletion) {
        info('El usuario necesita completar su perfil extendido');
      }

      return response;
    } catch (error) {
      const errorMessage = handleApiError(error, 'Error al iniciar sesión');
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Cerrar sesión
  const logout = async () => {
    setLoading(true);
    try {
      // Limpiar el temporizador de inactividad
      if (inactivityTimer) {
        clearInterval(inactivityTimer);
        setInactivityTimer(null);
      }

      await authService.logout();
      setUser(null);
      setIsVerified(true); // Resetear el estado de verificación

      // Redirigir al login
      navigate('/login');
    } catch (error) {
      warn('Error al cerrar sesión', { error: error.message });
      // Aún así, limpiamos el usuario local
      authService.clearAuth();
      setUser(null);
      setIsVerified(true); // Resetear el estado de verificación

      // Redirigir al login
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  // Actualizar perfil extendido
  const updateExtendedProfile = async (profileData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await userService.updateExtendedProfile(profileData);

      // Normalizar y actualizar el usuario en el estado con los datos del perfil extendido
      if (response.user) {
        const normalizedUser = {
          ...response.user,
          // Asegurar compatibilidad entre campos
          birthDate: response.user.birthDate || response.user.birthdate,
          birthdate: response.user.birthDate || response.user.birthdate,
          walletBalance: response.user.wallet || response.user.walletBalance || 0,
          isActive: response.user.active !== undefined ? response.user.active : response.user.isActive,
        };
        setUser(normalizedUser);
        // También actualizar en localStorage
        localStorage.setItem('prokids_user', JSON.stringify(normalizedUser));
      }

      // Actualizar la última actividad
      updateActivity();

      return response;
    } catch (error) {
      const errorMessage = handleApiError(error, 'Error al actualizar perfil extendido');
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Cambiar contraseña
  const changePassword = async (passwordData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.changePassword(passwordData);

      // Actualizar la última actividad
      updateActivity();

      return response;
    } catch (error) {
      const errorMessage = handleApiError(error, 'Error al cambiar la contraseña');
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Solicitar restablecimiento de contraseña
  const requestPasswordReset = async (email) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.requestPasswordReset(email);
      return response;
    } catch (error) {
      const errorMessage = handleApiError(
        error,
        'Error al solicitar el restablecimiento de contraseña',
      );
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Restablecer contraseña
  const resetPassword = async (token, newPassword) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.resetPassword(token, newPassword);
      return response;
    } catch (error) {
      const errorMessage = handleApiError(error, 'Error al restablecer la contraseña');
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Verificar si el usuario está autenticado
  const isAuthenticated = () => {
    return authService.isAuthenticated();
  };

  // Verificar si el usuario tiene un rol específico
  const hasRole = (role) => {
    return authService.hasRole(role);
  };

  // Verificar si el email del usuario está verificado
  const isEmailVerified = () => {
    return isVerified;
  };

  // Métodos de bloqueo eliminados - ahora se maneja en el backend

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        isVerified,
        register,
        login,
        logout,
        verifyEmail,
        resendVerificationEmail,
        updateExtendedProfile,
        changePassword,
        requestPasswordReset,
        resetPassword,
        isAuthenticated,
        hasRole,
        isEmailVerified,
        updateActivity,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Hook personalizado para usar el contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export default AuthContext;
